import { useState } from 'react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // סימולציה של שליחה לשרת
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // בדיקה שהשדות מלאים
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('אנא מלא את כל השדות הנדרשים');
      }

      // בדיקת אימייל תקין
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('אנא הכנס כתובת אימייל תקינה');
      }

      // סימולציה של הצלחה
      setSubmitStatus({ 
        type: 'success', 
        message: 'תודה! ההודעה נשלחה בהצלחה. נחזור אליך בהקדם האפשרי.' 
      });
      
      // איפוס הטופס
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.message || 'שגיאה בשליחת ההודעה. אנא נסה שוב.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#9a624b] via-[#ac609c] to-[#9a624b] bg-clip-text text-transparent mb-4">
            צור קשר
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            יש לך שאלה או בקשה? אנחנו כאן לעזור! צור איתנו קשר ונחזור אליך בהקדם האפשרי.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">שלח לנו הודעה</h2>
            
            {submitStatus && (
              <div className={`mb-6 p-4 rounded-lg ${
                submitStatus.type === 'success' 
                  ? 'bg-green-900/20 text-green-300 border border-green-500' 
                  : 'bg-red-900/20 text-red-300 border border-red-500'
              }`}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-800 mb-2">
                    שם מלא *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-accent rounded-lg focus:ring-2 focus:ring-brown focus:border-transparent transition-colors text-gray-800 placeholder-text-gray-600"
                    placeholder="הכנס את שמך המלא"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-2">
                    אימייל *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-accent rounded-lg focus:ring-2 focus:ring-brown focus:border-transparent transition-colors text-gray-800 placeholder-text-gray-600"
                    placeholder="your@email.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    טלפון
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#9B8A61] focus:border-transparent transition-colors text-white placeholder-gray-400"
                    placeholder="053-3332127"
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    נושא
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#9B8A61] focus:border-transparent transition-colors text-white placeholder-gray-400"
                    placeholder="נושא ההודעה"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  הודעה *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#9B8A61] focus:border-transparent transition-colors text-white placeholder-gray-400 resize-none"
                  placeholder="כתוב את הודעתך כאן..."
                  autoComplete="off"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#9a624b] to-[#ac609c] text-white py-4 px-6 rounded-lg hover:from-[#8b6f7c] hover:to-[#5b0c3d] transition-all duration-300 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    שולח...
                  </div>
                ) : (
                  'שלח הודעה'
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Store Info */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">מידע על החנות</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-brown rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-lg">📍</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">כתובת</h4>
                    <p className="text-gray-600">רחוב ירושלים 27, עפולה</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-brown rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-lg">📞</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">טלפון</h4>
                    <p className="text-gray-600">053-3332127</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-brown rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-lg">✉️</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">אימייל</h4>
                    <p className="text-gray-600">nataly@sha-sha.co.il</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-brown rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-lg">🕒</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">שעות פעילות</h4>
                    <p className="text-gray-600">א׳-ה׳: 09:00-19:00</p>
                    <p className="text-gray-600">ו׳: 09:00-14:00</p>
                    <p className="text-gray-600">שבת: סגור</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">שאלות נפוצות</h3>
              
              <div className="space-y-4">
                <div className="border-b border-gray-600 pb-4">
                  <h4 className="font-semibold text-gray-300 mb-2">איך אני יכול לבצע הזמנה?</h4>
                  <p className="text-gray-400 text-sm">
                    ניתן לבצע הזמנה דרך האתר או בטלפון. כל המוצרים זמינים לרכישה אונליין.
                  </p>
                </div>

                <div className="border-b border-gray-600 pb-4">
                  <h4 className="font-semibold text-gray-300 mb-2">מה מדיניות ההחזרות?</h4>
                  <p className="text-gray-400 text-sm">
                    אנו מקבלים החזרות תוך 14 ימים ממועד הרכישה, בתנאי שהמוצר במצב חדש.
                  </p>
                </div>

                <div className="border-b border-gray-600 pb-4">
                  <h4 className="font-semibold text-gray-300 mb-2">האם יש משלוחים?</h4>
                  <p className="text-gray-400 text-sm">
                    כן, אנו מספקים משלוחים לכל הארץ. המשלוח חינם בקנייה מעל 299₪.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-300 mb-2">האם המוצרים מקוריים?</h4>
                  <p className="text-gray-400 text-sm">
                    כל המוצרים שלנו הם בעבודת יד עם אבנים טבעיות אמיתיות.
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">עקבו אחרינו</h3>
              
              <div className="flex space-x-4 space-x-reverse">
                <a href="https://instagram.com/shasha_jewelry" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-r from-[#9a624b] to-[#ac609c] rounded-lg flex items-center justify-center text-white hover:from-[#8b6f7c] hover:to-[#5b0c3d] transition-all duration-300">
                  <span className="text-xl">📷</span>
                </a>
                <a href="https://facebook.com/shasha.jewelry" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-r from-[#9a624b] to-[#ac609c] rounded-lg flex items-center justify-center text-white hover:from-[#8b6f7c] hover:to-[#5b0c3d] transition-all duration-300">
                  <span className="text-xl">📘</span>
                </a>
                <a href="https://pinterest.com/shasha_jewelry" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-r from-[#9a624b] to-[#ac609c] rounded-lg flex items-center justify-center text-white hover:from-[#8b6f7c] hover:to-[#5b0c3d] transition-all duration-300">
                  <span className="text-xl">📌</span>
                </a>
                <a href="https://wa.me/972541234567" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-r from-[#9a624b] to-[#ac609c] rounded-lg flex items-center justify-center text-white hover:from-[#8b6f7c] hover:to-[#5b0c3d] transition-all duration-300">
                  <span className="text-xl">📱</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage; 